import { ambientAudio } from './audioSession'
import { CLIP_URLS, type SoundName } from './clips'

type AudioContextCtor = typeof AudioContext

/**
 * Plays the notification clips with the Web Audio API. Browsers only allow audio after a user gesture, so the
 * context is created and resumed on the first tap or key press; until then play() does nothing.
 */
export class SoundPlayer {
  private ctx: AudioContext | null = null
  private readonly buffers = new Map<SoundName, Promise<AudioBuffer | null>>()
  private listening = false

  constructor(
    private readonly createContext: () => AudioContext | null = defaultContextFactory,
    private readonly fetchClip: (url: string) => Promise<ArrayBuffer> = defaultFetch,
  ) {}

  /**
   * Unlocks audio on user interaction. The listeners stay attached: iOS interrupts the audio context whenever the
   * app goes to the background, and only another tap or key press may resume it. Once running they do nothing.
   */
  attach(target: Pick<Window, 'addEventListener'> = window): void {
    if (this.listening) return
    this.listening = true
    const handler = () => {
      if (!this.unlocked) void this.unlock()
    }
    for (const e of ['pointerdown', 'keydown', 'touchend'] as const) target.addEventListener(e, handler, { capture: true, passive: true })
  }

  get unlocked(): boolean {
    return this.ctx?.state === 'running'
  }

  async unlock(): Promise<boolean> {
    if (!this.ctx) {
      // Respect the iPhone silent switch (Safari's Audio Session API; ignored elsewhere).
      ambientAudio()
      this.ctx = this.createContext()
      if (!this.ctx) return false
    }
    if (this.ctx.state !== 'running') {
      try {
        await this.ctx.resume()
      } catch {
        return false
      }
    }
    // Each clip is fetched the first time it is needed and then kept: downloading all five on the first tap competed
    // with the pictures in the conversation for the connection, and a sound is only ever wanted one at a time.
    return this.ctx.state === 'running'
  }

  async play(name: SoundName, volume: number): Promise<boolean> {
    const ctx = this.ctx
    if (!ctx || ctx.state !== 'running') return false
    const buffer = await this.load(name)
    if (!buffer) return false
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const gain = ctx.createGain()
    gain.gain.value = Math.max(0, Math.min(1, volume))
    source.connect(gain).connect(ctx.destination)
    // Back to keeping to the silent switch after a clip, unless one is still playing.
    ambientAudio()
    source.start()
    return true
  }

  private load(name: SoundName): Promise<AudioBuffer | null> {
    const ctx = this.ctx
    if (!ctx) return Promise.resolve(null)
    let pending = this.buffers.get(name)
    if (!pending) {
      pending = this.fetchClip(CLIP_URLS[name])
        .then((data) => ctx.decodeAudioData(data))
        .catch(() => {
          this.buffers.delete(name) // allow a retry later, e.g. after coming back online
          return null
        })
      this.buffers.set(name, pending)
    }
    return pending
  }
}

function defaultContextFactory(): AudioContext | null {
  const w = window as Window & { webkitAudioContext?: AudioContextCtor }
  const Ctor = window.AudioContext ?? w.webkitAudioContext
  return Ctor ? new Ctor({ latencyHint: 'interactive' }) : null
}

async function defaultFetch(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`sound ${url}: ${res.status}`)
  return res.arrayBuffer()
}

export const soundPlayer = new SoundPlayer()
